import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export type ColorMode = 'light' | 'dark';

const COLOR_MODE_STORAGE_KEY = 'wisememo-color-mode';

/**
 * 深色模式 context（04-1.1）：
 * mode 初始化顺序 = localStorage → 系统偏好(prefers-color-scheme) → light；
 * 切换写 localStorage 并触发主题重建。
 */
const getInitialColorMode = (): ColorMode => {
  try {
    const stored = window.localStorage.getItem(COLOR_MODE_STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
  } catch {
    // localStorage 不可用时忽略
  }
  if (typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
};

interface ColorModeContextValue {
  mode: ColorMode;
  toggleColorMode: () => void;
}

const ColorModeContext = createContext<ColorModeContextValue>({
  mode: 'light',
  toggleColorMode: () => {},
});

export function ColorModeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setMode] = useState<ColorMode>(getInitialColorMode);

  useEffect(() => {
    try {
      window.localStorage.setItem(COLOR_MODE_STORAGE_KEY, mode);
    } catch {
      // 忽略持久化失败
    }
    // 同步到 DOM 属性，供 CSS（memo 正文/阅读页等非 MUI 样式）做深色适配，
    // 与 memo-flutter 深色主题（cc-dark.json + ColorScheme 派生）对齐
    document.documentElement.dataset.colorMode = mode;
  }, [mode]);

  const value = useMemo<ColorModeContextValue>(
    () => ({
      mode,
      toggleColorMode: () => setMode((m) => (m === 'dark' ? 'light' : 'dark')),
    }),
    [mode]
  );

  return <ColorModeContext.Provider value={value}>{children}</ColorModeContext.Provider>;
}

export function useColorMode() {
  return useContext(ColorModeContext);
}
